import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { api, apiFetch } from '../../services/api';
import { useNotifications } from '../../contexts/NotificationContext';
import {
  Form,
  FormSection,
  FormActions,
  Input,
  Textarea,
  Button,
  Checkbox
} from '../ui/StandardComponents';
import { generateSeoFilename } from '../../utils/seoUtils';
import { Banner } from '../../types/types';
import { BrandLoader } from '../ui/BrandLoader';
import { logger } from '../../utils/logger';
import { useFormDraft } from '../../hooks/useFormDraft';
import { DraftRestoreBanner } from '../ui/DraftRestoreBanner';

// Achado (auditoria do design system): o <input type="file"> ficava `hidden` (display:none) dentro do
// <label> estilizado como botão — não recebia foco, então o upload era inalcançável pelo teclado.
// `sr-only` mantém o input focável; o anel de foco aparece no label via focus-within.
const UPLOAD_LABEL_CLASSES =
  'cursor-pointer inline-flex items-center justify-center rounded-md text-sm font-medium ring-offset-background transition-colors focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-4 py-2';


// Schema
const bannerSchema = z.object({
  title: z.string().min(1, 'Título é obrigatório'),
  description: z.string(),
  imageUrl: z.string().min(1, 'URL da imagem é obrigatória'),
  mobileImageUrl: z.string(),
  linkUrl: z.string(),
  sortOrder: z.number(),
  active: z.boolean(),
});

type BannerFormData = z.infer<typeof bannerSchema>;

interface BannerFormProps {
  initialData?: Partial<Banner> | null;
  onSuccess: () => void;
  onCancel: () => void;
  /** Notifica o formulário pai sobre alterações não salvas, para a trava de fechamento
   * acidental (ver useUnsavedChangesGuard, usado em BannerManagementPage). */
  onDirtyChange?: (dirty: boolean) => void;
}

export const BannerForm: React.FC<BannerFormProps> = ({ initialData, onSuccess, onCancel, onDirtyChange }) => {
  const { addNotification } = useNotifications();
  const [loading, setLoading] = useState(false);
  const [uploadingDesktop, setUploadingDesktop] = useState(false);
  const [uploadingMobile, setUploadingMobile] = useState(false);
  const isEditing = Boolean(initialData && initialData.id);

  const draftKey = `xp-draft-banner-${initialData?.id || 'new'}`;
  const { save: saveDraft, load: loadDraft, clear: clearDraft } = useFormDraft<BannerFormData>(draftKey);
  const [draftPrompt, setDraftPrompt] = useState<{ values: BannerFormData; savedAt: number } | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<BannerFormData>({
    resolver: zodResolver(bannerSchema),
    defaultValues: {
      title: '',
      description: '',
      imageUrl: '',
      mobileImageUrl: '',
      linkUrl: '',
      sortOrder: 0,
      active: true,
    },
  });

  useEffect(() => {
    if (initialData) {
      reset({
        title: initialData.title || '',
        description: initialData.description || '',
        imageUrl: initialData.imageUrl || '',
        mobileImageUrl: initialData.mobileImageUrl || '',
        linkUrl: initialData.linkUrl || '',
        sortOrder: initialData.sortOrder || 0,
        active: initialData.active ?? true,
      });
    } else {
        reset({
            active: true,
            sortOrder: 0
        });
    }

    const draft = loadDraft();
    if (draft) setDraftPrompt(draft);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, reset]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const watchedValues = watch();
  useEffect(() => {
    if (!isDirty) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => saveDraft(watchedValues), 500);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(watchedValues), isDirty]);

  const handleRestoreDraft = () => {
    if (!draftPrompt) return;
    reset(draftPrompt.values);
    setDraftPrompt(null);
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setDraftPrompt(null);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, field: 'imageUrl' | 'mobileImageUrl') => {
    const file = e.target.files?.[0];
    if (!file) return;

    const setUploading = field === 'imageUrl' ? setUploadingDesktop : setUploadingMobile;
    setUploading(true);

    try {
      const currentTitle = getValues('title') || 'novo-banner';
      const seoFilename = generateSeoFilename('banners', currentTitle, field === 'mobileImageUrl' ? 'mobile' : 'desktop');

      const formData = new FormData();
      formData.append('image', file);
      formData.append('folder', 'banners');
      formData.append('fileName', seoFilename);

      // Using raw axios from 'api' because simple apiFetch might not handle FormData automagically the same way everywhere or we prefer explicit content-type
      // Actually standardizing on apiFetch is better but I saw axios usage in previous file. 
      // Using api.post as in previous file.
      const res = await api.post('/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const url = res.data.imageUrl || res.data.url;
      if (url) {
        setValue(field, url);
        addNotification({ type: 'success', title: 'Upload concluído', message: 'Imagem carregada com sucesso' });
      } else {
        throw new Error('URL não retornada');
      }
    } catch (error) {
      logger.error('Erro', 'BannerForm', error);
      addNotification({ type: 'error', title: 'Erro', message: 'Falha no upload da imagem' });
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (data: BannerFormData) => {
    try {
      setLoading(true);
      // Ensure numeric
       const payload = {
            ...data,
            sortOrder: Number(data.sortOrder)
       };

      if (isEditing && initialData?.id) {
        await apiFetch(`/banners/${initialData.id}`, {
            method: 'PUT',
            body: JSON.stringify(payload)
        });
        addNotification({ type: 'success', title: 'Sucesso', message: 'Banner atualizado.' });
      } else {
        await apiFetch(`/banners`, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        addNotification({ type: 'success', title: 'Sucesso', message: 'Banner criado.' });
      }
      clearDraft();
      onSuccess();
    } catch (error) {
      logger.error('Erro', 'BannerForm', error);
      addNotification({ type: 'error', title: 'Erro', message: 'Falha ao salvar banner.' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <BrandLoader size={80} label="Salvando..." />;

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {draftPrompt && (
        <DraftRestoreBanner
          savedAt={draftPrompt.savedAt}
          onRestore={handleRestoreDraft}
          onDiscard={handleDiscardDraft}
        />
      )}
      <FormSection title="Informações Básicas" description="Defina os textos principais do banner">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="col-span-2">
            <Input
              label="Título"
              placeholder="Digite o título do banner"
              {...register('title')}
              error={errors.title?.message}
            />
          </div>
          <div className="col-span-2">
            <Textarea
              label="Descrição"
              placeholder="Descrição ou subtítulo (opcional)"
              {...register('description')}
              error={errors.description?.message}
            />
          </div>
        </div>
      </FormSection>

      <FormSection title="Imagens" description="Faça upload ou cole a URL das imagens">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
                <Input
                    label="Imagem Desktop (URL)"
                    placeholder="https://..."
                    {...register('imageUrl')}
                    error={errors.imageUrl?.message}
                />
                <div className="flex items-center gap-2">
                     <label className={UPLOAD_LABEL_CLASSES}>
                        {uploadingDesktop ? 'Enviando...' : 'Enviar imagem (desktop)'}
                        <input 
                            type="file" 
                            className="sr-only" 
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'imageUrl')}
                            disabled={uploadingDesktop}
                        />
                     </label>
                </div>
            </div>

            <div className="space-y-2">
                <Input
                    label="Imagem Mobile (URL - Opcional)"
                    placeholder="https://..."
                    {...register('mobileImageUrl')}
                    error={errors.mobileImageUrl?.message}
                />
                <div className="flex items-center gap-2">
                     <label className={UPLOAD_LABEL_CLASSES}>
                        {uploadingMobile ? 'Enviando...' : 'Enviar imagem (celular)'}
                        <input 
                            type="file" 
                            className="sr-only" 
                            accept="image/*"
                            onChange={(e) => handleImageUpload(e, 'mobileImageUrl')}
                            disabled={uploadingMobile}
                        />
                     </label>
                </div>
            </div>
        </div>
      </FormSection>

      <FormSection title="Configurações" description="Link, ordenação e status">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
             <Input
                label="Link de Destino"
                placeholder="/promocoes/verao"
                {...register('linkUrl')}
                error={errors.linkUrl?.message}
             />
             <Input
                type="number"
                label="Ordem de Exibição"
                {...register('sortOrder')}
                error={errors.sortOrder?.message}
             />
             <div className="col-span-2 pt-4">
                <Checkbox id="active" label="Banner Ativo" {...register('active')} />
             </div>
        </div>
      </FormSection>

      <FormActions>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary">
          {isEditing ? 'Salvar Alterações' : 'Criar Banner'}
        </Button>
      </FormActions>
    </Form>
  );
};
